import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  PayrollService,
  PayrollTimesheet,
  PayrollTimesheetStatus,
  PayrollDayRecord,
  PayrollPrime,
  Payslip
} from '../../../services/payroll.service';
import { FlagService } from '../../../shared/services/flag.service';
import { FlagType } from '../../../shared/utils/enums';

@Component({
  selector: 'app-payroll-period-detail',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './payroll-period-detail.component.html',
  styleUrl: './payroll-period-detail.component.scss'
})
export class PayrollPeriodDetailComponent implements OnInit {
  periodId!: number;
  timesheets: PayrollTimesheet[] = [];
  payslips: Payslip[] = [];
  loading = false;
  sending = false;
  refreshingPayslips = false;
  expandedId: number | null = null;

  constructor(
    private service: PayrollService,
    private route: ActivatedRoute,
    private router: Router,
    private flag: FlagService
  ) {}

  ngOnInit(): void {
    this.periodId = Number(this.route.snapshot.paramMap.get('id'));
    this.load();
  }

  load(): void {
    this.loading = true;
    this.service.timesheets(this.periodId).subscribe({
      next: (data) => {
        this.timesheets = data ?? [];
        this.loading = false;
      },
      error: () => {
        this.flag.displayFlag('Erreur lors du chargement des relevés', FlagType.error);
        this.loading = false;
      }
    });
    this.service.payslips(this.periodId).subscribe({
      next: (data) => (this.payslips = data ?? []),
      error: () => {}
    });
  }

  back(): void {
    this.router.navigate(['/agency/payroll']);
  }

  toggle(sheet: PayrollTimesheet): void {
    this.expandedId = this.expandedId === sheet.id ? null : sheet.id;
  }

  days(sheet: PayrollTimesheet): PayrollDayRecord[] {
    try {
      return JSON.parse(sheet.dailyRecordsJson || '[]');
    } catch {
      return [];
    }
  }

  primes(sheet: PayrollTimesheet): PayrollPrime[] {
    try {
      return JSON.parse(sheet.primesJson || '[]');
    } catch {
      return [];
    }
  }

  get totalHours(): number {
    return Math.round(this.timesheets.reduce((sum, t) => sum + (t.totalHours || 0), 0) * 100) / 100;
  }

  get pendingCount(): number {
    return this.timesheets.filter((t) => t.status !== 'SENT').length;
  }

  sendToSage(): void {
    if (this.sending || this.pendingCount === 0) return;
    this.sending = true;
    this.service.sendToSage(this.periodId).subscribe({
      next: () => {
        this.flag.displayFlag('Relevés transmis à Sage Paie', FlagType.success);
        this.sending = false;
        this.load();
      },
      error: () => {
        this.flag.displayFlag("Erreur lors de l'envoi à Sage", FlagType.error);
        this.sending = false;
      }
    });
  }

  refreshPayslips(): void {
    if (this.refreshingPayslips) return;
    this.refreshingPayslips = true;
    this.service.refreshPayslips(this.periodId).subscribe({
      next: (data) => {
        this.payslips = data ?? [];
        this.refreshingPayslips = false;
        if (this.payslips.length === 0) {
          this.flag.displayFlag('Aucun bulletin disponible côté Sage pour cette période', FlagType.warning);
        }
      },
      error: () => {
        this.flag.displayFlag('Erreur lors de la récupération des bulletins', FlagType.error);
        this.refreshingPayslips = false;
      }
    });
  }

  downloadPayslip(payslip: Payslip): void {
    this.service.downloadPayslipPdf(payslip.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      },
      error: () => {
        this.flag.displayFlag('PDF indisponible (intégration Sage non encore active)', FlagType.error);
      }
    });
  }

  statusLabel(status: PayrollTimesheetStatus): string {
    switch (status) {
      case 'DRAFT': return 'Brouillon';
      case 'READY': return 'Vérifié';
      case 'SENT': return 'Envoyé';
      case 'ERROR': return 'Erreur';
      default: return status;
    }
  }
}
